import { useContext, useEffect, useReducer, useState } from "react";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { _sessionManager } from "api/ApiHelper";
import { AppContext } from "App";
import { BUDGET_STATUS_ACTIVE } from "components/Budget/helpers";
import EmptyCard from "components/Cards/EmptyCard";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { FarmVaultIcon } from "components/Icons/Icons";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { useSnackbar } from "notistack";
import { formatPrice } from "utility/formatters";

import { ReportsIcon } from "../../Icons/Icons";
import { sortAmount } from "./helpers";
import { ReportViewer } from "./ReportViewer";

export default function InsuranceSummaryReportWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const { enqueueSnackbar } = useSnackbar();
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || err, { preventDuplicate: true, variant: "error" });
  };

  const [currentYear, setCurrentYear] = useState(
    globalState?.crop_year ? globalState?.crop_year : getCurrentReinsuranceYear()
  );
  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
    insuranceSummaryReport();
  }, [currentYear]);

  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    commodity_name: [],
    practice_name: [],
  });

  const [insuranceSummaryReportData, setInsuranceSummaryReportData] = useState([]);
  const [filteredInsuranceSummaryReportData, setFilteredInsuranceSummaryReportData] = useState([]);

  const insuranceSummaryReport = async () => {
    try {
      if (globalState?.producer_token) {
        const _filter = {
          producer_token: globalState?.producer_token,
          is_active: true,
          status: BUDGET_STATUS_ACTIVE,
          year: currentYear,
        };

        const api = new ApiHelper({
          baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
          config: {},
          sessionManager: _sessionManager,
          // No caching:
          _instance: null,
        });
        const _dataDictionary = await api.callPost(
          "data/Users/Insurance/InsuranceSummaryReport/ExportObjectGraph",
          _filter,
          globalState
        );
        setInsuranceSummaryReportData(transformInsuranceData(_dataDictionary));
        setFilteredInsuranceSummaryReportData(transformInsuranceData(_dataDictionary));
      }
    } catch (err) {
      handleError(err);
    }
  };

  // Filter the data based on the filter state
  useEffect(() => {
    const filteredData = insuranceSummaryReportData.filter((item) => {
      const commodityFilter = !filter?.commodity_name?.length || filter?.commodity_name?.includes(item.commodity_name);
      const practiceFilter = !filter?.practice_name?.length || filter?.practice_name?.includes(item.practice_name);
      return commodityFilter && practiceFilter;
    });
    setFilteredInsuranceSummaryReportData(filteredData);
  }, [insuranceSummaryReportData, filter]);

  // Transform the data to match the columnsDirective
  const transformInsuranceData = (originalData) => {
    return originalData?.flatMap((item) =>
      item.insuranceplan?.map((plan) => ({
        ...plan,
        location_state_name: plan.state_name,
        location_county_name: plan.county_name,
        producer_farm_field_id: plan.insurance_scenario_id,
        crop_year: plan.reinsurance_year,
        coverage_level_percent: parseFloat(plan.coverage_level_percent),
        insured_share_percent: parseFloat(plan.insured_share_percent),
        liability_amount: formatPrice(plan.liability_amount, 0),
        producer_premium_amount: formatPrice(plan.producer_premium_amount, 0),
        producer_premium_amount_per_acre: formatPrice(plan.producer_premium_amount_per_acre, 2),
      }))
    );
  };
  const columnsDirective = [
    { field: "location_state_name", headerText: "State", width: 100 },
    { field: "location_county_name", headerText: "County", width: 100 },
    { field: "commodity_name", headerText: "Crop", width: 100 },
    { field: "insurance_plan_name", headerText: "Insurance Plan Name", width: 100, textAlign: "Right" },
    {
      field: "coverage_level_percent",
      headerText: "Coverage Level Percent",
      width: 100,
      textAlign: "Right",
      format: "P0",
    },
    {
      field: "insured_share_percent",
      headerText: "Insured Share Percent",
      width: 100,
      textAlign: "Right",
      format: "P2",
    },
    {
      field: "liability_amount",
      headerText: "Liability Amount",
      width: 150,
      textAlign: "Right",
      sortComparer: sortAmount,
    },
    { field: "commodity_name", headerText: "Commodity", width: 150 },
    { field: "type_name", headerText: "Type", width: 150 },
    { field: "practice_name", headerText: "Practice", width: 150 },
    {
      field: "producer_premium_amount",
      headerText: "Producer Premium Amount",
      width: 150,
      textAlign: "Right",
      sortComparer: sortAmount,
    },
    {
      field: "producer_premium_amount_per_acre",
      headerText: "Per Acre",
      width: 150,
      textAlign: "Right",
      sortComparer: sortAmount,
    },
  ];
  return (
    <>
      <WorkspaceHeader
        title="Insurance Summary"
        category={{
          title: "Reports",
          icon: <ReportsIcon />,
        }}
      />

      <ReportViewer
        columnsDirective={columnsDirective}
        counties={filteredInsuranceSummaryReportData}
        producerFarmFieldCrops={filteredInsuranceSummaryReportData}
        isLoadingCounties={false}
        reportType="insurance"
        currentYear={currentYear}
        setCurrentYear={setCurrentYear}
        gridKey="InsuranceSummaryReport"
        filter={filter}
        dispatchFilter={dispatchFilter}
        isVerticalPdfExport={true}
      />

      {!filteredInsuranceSummaryReportData?.length && (
        <>
          <EmptyCard text="No search results" secondaryText="Remove some filters or add a new farm in the Farm Vault" />
        </>
      )}
    </>
  );
}
